<template>
    <div>
        <div>
            <ding-single-select
                v-model="address.country"
                :preselect-first="true"
                :options="countryOptions"
                value-attribute="value" label-attribute="label"/>
        </div>
        <div style="display:grid;grid-template-columns: 1fr 1fr 1fr;gap:8px;margin-top:8px;">
            <ding-single-select
                v-model="address.admin_level_1"
                :preselect-first="false"
                :options="regionOptions"
                value-attribute="value" label-attribute="label"/>

            <ding-single-select
                v-model="address.admin_level_2"
                :preselect-first="false"
                :options="level2Options"
                value-attribute="value" label-attribute="label"/>
            <div>
                <input v-model="address.postal_code" class="form-control" :placeholder="$t('postal_code_placeholder')" />
            </div>
        </div>
        <div class="mt-2">
            <input v-model="address.address" class="form-control form-control-sm w-100" :placeholder="$t('please_enter_address')" />
        </div>
    </div>
</template>

<script>
import api from '../../mixins/api';
import loadingIndicator from '../common/LoadingIndicator';
import promptConfirm from '../../mixins/prompt-confirm';
import DingSingleSelect from './DingSingleSelect';

export default {
    mixins: [api, promptConfirm],
    components: { loadingIndicator, DingSingleSelect },
    props: {
        originalAddress: { required: true, type: Object },
        regionLimitation: { type: String, default: 'NONE'} // NONE, TAIWAN, OFF_ISLAND
    },
    mounted() {
        this.prepareRegions()
    },
    data() {
        return {
            address: Object.assign({}, this.originalAddress),
            countries: ['taiwan'],
            regions: {}
        }
    },
    watch: {
        'address.country'() {
            var emitValue = this.address;
            emitValue.admin_level_1 = null;
            emitValue.admin_level_2 = null;
            emitValue.admin_level_3 = null;
            emitValue.postal_code = null;
            this.$emit('input', emitValue);
        },
        'address.admin_level_1'() {
            var emitValue = this.address;
            emitValue.admin_level_2 = null;
            emitValue.admin_level_3 = null;
            emitValue.postal_code = null;
            this.$emit('input', emitValue);
        },
        'address.admin_level_2'() {
            var emitValue = this.address;
            emitValue.admin_level_3 = null;
            this.$emit('input', emitValue);
            this.getPostalCode();
        },
        'address.address'() {
            this.$emit('input', this.address);
        },
        'address.admin_level_3'() {
            this.$emit('input', this.address);
        },
        regionOptions(newValue, oldValue) {
            const noChange = (newValue.length == oldValue.length) && newValue.every(newItem => {
                return oldValue.find(oldItem => newItem === oldItem);
            });
            if (oldValue.length && !noChange) {
                this.clearAdminLevels();
            }
        }
    },
    computed: {
        level2Options() {
            return this.adminLevel2Regions(this.address.country, this.address.admin_level_1).map((r) => {
                return {value: r, label: r};
            });
        },
        regionOptions() {
            if (!this.address.country || !this.regions[this.address.country]) {
                return [];
            }
            const offIslands = ['金門縣', '澎湖縣', '連江縣', '南海諸島'];
            return Object.keys(this.regions[this.address.country])
                .filter((r) => {
                    if (this.regionLimitation === 'TAIWAN') {
                        return !(offIslands.indexOf(r) >= 0);
                    } else if (this.regionLimitation === 'OFF_ISLAND') {
                        return offIslands.indexOf(r) >= 0;
                    }
                })
                .map((r) => {
                    return {
                        value: r,
                        label: r
                    };
                });
        },
        countryOptions() {
            return this.countries.map((c, i) => {
                return {
                    value: c,
                    label: this.$t('country.' + c)
                }
            });
        },
        isEmpty() {
            for (var key in this.address) {
                if (key === 'country') {
                    continue;
                }

                if (this.address[key]) {
                    return false;
                }
            }
            return true;
        }
    },
    methods: {
        clearAdminLevels() {
            this.address.postal_code = null;
            this.address.admin_level_1 = null;
            this.address.admin_level_2 = null;
            this.address.admin_level_3 = null;
        },
        clearAdminLevel2() {
            this.address.admin_level_2 = null;
            this.address.admin_level_3 = null;
        },
        adminLevel2Regions(country, adminLevel1) {
            if (this.regions && this.regions[country] && this.regions[country][adminLevel1]) {
                return this.regions[country][adminLevel1];
            }
            return [];
        },
        prepareRegions() {
            for (var i in this.countries) {
                let c = this.countries[i];
                this.getRegions(c, (response, error) => {
                    if (response && response.data) {
                        Vue.set(this.regions, c, response.data);
                    }
                    this.$forceUpdate();
                })
            }
        },
        getPostalCode() {
            const country = this.address.country;
            const city = this.address.admin_level_1;
            const town = this.address.admin_level_2;
            if (country && city && town) {
                this.getRegionPostalCode(country, city, town, (response, error) => {
                    if (error) {
                        this.promptAlert(error);
                    } else {
                        this.address.postal_code = response.data;
                        this.$forceUpdate();
                        this.$emit('input', this.address);
                    }
                })
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.address-container {
    display: flex;
}

input {
    border: none;
    padding: 0.375rem 1.2rem !important;
    background: #F2F7F8;
    height: auto !important;
}
</style>

<i18n>
{
    "zh": {
        "customer_editor": "顧客資料",
        "loading": "取得顧客資料中...",
        "add_new_address": "新增地址",
        "please_enter_address": "輸入地址",
        "address": "地址",
        "no_address_set": "尚無資料",
        "postal_code_placeholder": "郵遞區號"
    },
    "en": {
        "customer_editor": "Customer Information",
        "loading": "Loading Customer Data...",
        "add_new_address": "Add New Address",
        "please_enter_address": "address",
        "address": "Address",
        "no_address_set": "No address set yet.",
        "postal_code_placeholder": "postal code"
    }
}
</i18n>